import type { PageItem } from '@/db/content';
import {
  getRootPathsAndDefaultLocale,
  getUrlForItem,
  indexPageItemsByTranslationKey,
  localePrefixedPublicUrlFromDefault,
} from '@/navTree';

const MATERIALIZED_PATH_SECTION_LENGTH = 4;

/** Parent materialized path (Wagtail treebeard): strip last 4-char segment. */
export function getMaterializedParentPath(path: string | undefined): string | null {
  if (!path || path.length <= MATERIALIZED_PATH_SECTION_LENGTH) return null;
  return path.slice(0, -MATERIALIZED_PATH_SECTION_LENGTH);
}

export type NormalPageContentSidebarItem = {
  id: number;
  title: string;
  url: string;
  icon?: string | null;
};

export type NormalPageContentSidebarModel = {
  title: string;
  items: NormalPageContentSidebarItem[];
};

/** Shallowest HomePage for a Wagtail locale (canonical site root for that tree). */
function shallowestHomePageForLocale(items: PageItem[], locale: string): PageItem | null {
  const homes = items.filter(
    (i) => i.meta.type === 'home.HomePage' && i.meta.locale === locale
  );
  if (!homes.length) return null;
  return homes.reduce((a, b) => {
    const da = (a.meta.url_path ?? '').split('/').filter(Boolean).length;
    const db = (b.meta.url_path ?? '').split('/').filter(Boolean).length;
    return da <= db ? a : b;
  });
}

/** Default-locale NormalPage for the same translation_key, else the viewed row. */
function canonicalNormalPageForSidebar(
  viewed: PageItem,
  defaultLocale: string,
  byKey: Map<string, Map<string, PageItem>>
): PageItem {
  const tk = viewed.meta.translation_key;
  if (!tk) return viewed;
  const defRow = byKey.get(tk)?.get(defaultLocale);
  if (defRow?.meta.type === 'home.NormalPage') return defRow;
  return viewed;
}

function localizeSidebarRow(
  canonical: PageItem,
  displayLocale: string,
  byKey: Map<string, Map<string, PageItem>>,
  rootPathByLocale: Map<string, string>,
  defaultLocale: string
): NormalPageContentSidebarItem {
  const key = canonical.meta.translation_key;
  let title = canonical.title;
  let url = getUrlForItem(canonical, rootPathByLocale, defaultLocale);
  let id = canonical.id;
  let icon: string | null | undefined = canonical.meta.icon;

  if (key) {
    const translated = byKey.get(key)?.get(displayLocale);
    if (translated) {
      title = translated.title;
      url = getUrlForItem(translated, rootPathByLocale, defaultLocale);
      id = translated.id;
      icon = translated.meta.icon ?? icon;
    } else if (displayLocale !== defaultLocale) {
      url = localePrefixedPublicUrlFromDefault(url, displayLocale, defaultLocale);
    }
  } else if (displayLocale !== defaultLocale && canonical.meta.locale === defaultLocale) {
    url = localePrefixedPublicUrlFromDefault(url, displayLocale, defaultLocale);
  }

  return {
    id,
    title,
    url,
    icon: icon ?? null,
  };
}

/**
 * Section sidebar for NormalPage "content" header: [section root, …its NormalPage children].
 * **Structure** follows the default-locale NormalPage tree for the same `translation_key` as the
 * viewed page (so every translation shows the same sibling set). **Titles and URLs** follow
 * `displayLocale` (translations or synthetic prefixed URLs), matching main nav behaviour.
 */
export function buildNormalPageContentSidebar(
  displayLocale: string,
  pageId: number,
  items: PageItem[],
  siteRootId: number | undefined
): NormalPageContentSidebarModel | null {
  const currentViewed = items.find(
    (item) => item.id === pageId && item.meta.type === 'home.NormalPage'
  );
  if (!currentViewed) return null;

  const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
  const byKey = indexPageItemsByTranslationKey(items);
  const canonicalPage = canonicalNormalPageForSidebar(currentViewed, defaultLocale, byKey);

  const treeLocale = canonicalPage.meta.locale;
  const siteRootForTree = shallowestHomePageForLocale(items, treeLocale);
  const siteRootIdForTree = siteRootForTree?.id ?? siteRootId;
  if (siteRootIdForTree == null) return null;

  const parentPath = getMaterializedParentPath(canonicalPage.meta.path);
  if (parentPath == null) return null;

  const parent = items.find(
    (item) => item.meta.locale === treeLocale && item.meta.path === parentPath
  );
  if (!parent) return null;

  const sectionRoot = parent.id === siteRootIdForTree ? canonicalPage : parent;

  const children = items
    .filter((item) => {
      if (item.meta.locale !== treeLocale) return false;
      if (item.meta.type !== 'home.NormalPage') return false;
      return getMaterializedParentPath(item.meta.path) === sectionRoot.meta.path;
    })
    .sort((a, b) => a.meta.path.localeCompare(b.meta.path));

  const rows: PageItem[] = [sectionRoot, ...children];

  const sectionHeader = localizeSidebarRow(
    sectionRoot,
    displayLocale,
    byKey,
    rootPathByLocale,
    defaultLocale
  );

  return {
    title: sectionHeader.title,
    items: rows.map((item) =>
      localizeSidebarRow(item, displayLocale, byKey, rootPathByLocale, defaultLocale)
    ),
  };
}
